/** Persisted request lifecycle shared by the composer and thread summaries.
 * Adapted from T3 Code #10123: terminal events win regardless of replay order.
 */
interface RequestActivity {
  readonly kind: string;
  readonly payload: unknown;
}

const staleRequestDetails = {
  approval: [
    "stale pending approval request",
    "unknown pending approval request",
    "unknown pending permission request",
    "unknown pending codex approval request",
  ],
  "user-input": [
    "stale pending user-input request",
    "unknown pending user-input request",
    "unknown pending user input request",
    "unknown pending codex user input request",
  ],
} as const;

function pendingRequests<T extends RequestActivity>(
  activities: ReadonlyArray<T>,
  kind: keyof typeof staleRequestDetails,
): Array<{ requestId: string; activity: T }> {
  const pending = new Map<string, T>();
  const closed = new Set<string>();
  for (const activity of activities) {
    if (
      activity.kind !== `${kind}.requested` &&
      activity.kind !== `${kind}.resolved` &&
      activity.kind !== `provider.${kind}.respond.failed`
    )
      continue;
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId = payload?.requestId;
    if (typeof requestId !== "string" || requestId.length === 0) continue;
    if (activity.kind === `${kind}.requested`) {
      if (!closed.has(requestId)) pending.set(requestId, activity);
    } else {
      const detail = typeof payload?.detail === "string" ? payload.detail.toLowerCase() : "";
      if (
        activity.kind === `${kind}.resolved` ||
        staleRequestDetails[kind].some((fragment) => detail.includes(fragment))
      ) {
        closed.add(requestId);
        pending.delete(requestId);
      }
    }
  }
  return Array.from(pending, ([requestId, activity]) => ({ requestId, activity }));
}

export function pendingUserInputRequests<T extends RequestActivity>(activities: ReadonlyArray<T>) {
  return pendingRequests(activities, "user-input");
}

export function pendingApprovalRequests<T extends RequestActivity>(activities: ReadonlyArray<T>) {
  return pendingRequests(activities, "approval");
}
